import { AlertTriangle, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ScoringRule } from "@/api/types";

interface ScoringRuleSpec {
  key: string;
  label: string;
  max_points: number;
}

interface ScoreBreakdownProps {
  totalScore: number | null;
  scoreDetails: Record<string, unknown> | null;
  /** Per-company metadata bag — used as a fallback source when
   *  `score_details` doesn't carry a value for a rule. The CFO
   *  scoring engine fills score_details directly; M&A imports
   *  drop the per-rule scores into metadata under keys like
   *  `finance_score` / `governance_score` / `market_score`. */
  metadata?: Record<string, unknown> | null;
  scoringRules: Array<ScoringRule | Record<string, unknown>>;
  scoreOverride?: number | null;
  onRemoveOverride?: () => Promise<void> | void;
}

/** Maps a scoring-rule `key` to the metadata field that carries
 *  its score for project types that don't run through the
 *  Prism scoring engine (e.g. M&A imports). The fallback is
 *  consulted only when `score_details` doesn't have the key. */
const RULE_KEY_TO_METADATA_KEY: Record<string, string> = {
  financial_health: "finance_score",
  governance_succession: "governance_score",
  market_dynamics: "market_score",
};

export function ScoreBreakdown({
  totalScore,
  scoreDetails,
  metadata,
  scoringRules,
  scoreOverride = null,
  onRemoveOverride,
}: ScoreBreakdownProps) {
  /** Resolve the earned points for a rule. Walks score_details
   *  first (CFO engine output), then falls through to the
   *  metadata fallback for project types that don't compute
   *  scores in Prism. Returns 0 if neither source carries a
   *  finite number. */
  const resolveEarned = (ruleKey: string): number => {
    const fromDetails = scoreDetails?.[ruleKey];
    if (typeof fromDetails === "number" && Number.isFinite(fromDetails)) {
      return fromDetails;
    }
    const metadataKey = RULE_KEY_TO_METADATA_KEY[ruleKey];
    if (metadataKey && metadata) {
      const fromMeta = metadata[metadataKey];
      const n =
        typeof fromMeta === "number"
          ? fromMeta
          : typeof fromMeta === "string"
            ? Number(fromMeta)
            : NaN;
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };
  const rules = (scoringRules as ScoringRuleSpec[]).filter(
    (r) => r && typeof r === "object" && "key" in r && "max_points" in r,
  );
  const totalMax = rules.reduce((sum, r) => sum + (r.max_points ?? 0), 0);
  const excluded = Array.isArray(scoreDetails?._excluded)
    ? (scoreDetails!._excluded as string[])
    : [];

  if (rules.length === 0) {
    return (
      <p className="text-sm text-slate-400">No scoring rules defined.</p>
    );
  }

  return (
    <div className="space-y-4">
      {scoreOverride !== null && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/40 p-3">
          <Pencil className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="flex-1">
            <div className="text-sm">
              <span className="font-medium text-amber-900">
                Manual score: {scoreOverride}
              </span>
              <span className="text-amber-700">
                {" "}
               , calculated score ({totalScore ?? 0}) is ignored
              </span>
            </div>
            {onRemoveOverride && (
              <button
                type="button"
                onClick={() => void onRemoveOverride()}
                className="mt-1 text-xs font-medium text-amber-700 underline-offset-2 hover:underline"
              >
                Remove override
              </button>
            )}
          </div>
        </div>
      )}

      {(() => {
        // Sum per-rule earned points so the header always shows a
        // total derived from the same source as the bars below.
        // When the backend's totalScore is null (e.g. M&A imports
        // with metadata-only scores), this falls back to summing
        // the resolved values.
        const summed = rules.reduce(
          (sum, r) => sum + resolveEarned(r.key),
          0,
        );
        const headerTotal = totalScore ?? summed;
        return (
          <div className="flex items-baseline justify-between">
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Signal intelligence
            </h3>
            <span className="text-xs font-semibold text-indigo-600">
              {headerTotal}
              <span className="text-slate-400">/{totalMax}</span>
              <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-400">
                total
              </span>
            </span>
          </div>
        );
      })()}

      <div className="space-y-3">
        {rules.map((rule) => {
          const earned = resolveEarned(rule.key);
          const max = rule.max_points || 1;
          const pct = Math.max(0, Math.min(1, earned / max));
          const fillColor = barColor(pct, earned);

          return (
            <div key={rule.key} className="space-y-1.5">
              <div className="flex items-baseline justify-between text-sm">
                <span className="truncate text-slate-700">{rule.label}</span>
                <span
                  className={cn(
                    "ml-3 font-medium tabular-nums",
                    earned === 0
                      ? "text-slate-400"
                      : pct >= 0.75
                        ? "text-emerald-600"
                        : pct >= 0.4
                          ? "text-indigo-600"
                          : "text-amber-600",
                  )}
                >
                  {earned}
                  <span className="text-slate-400">/{rule.max_points}</span>
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    fillColor,
                  )}
                  style={{ width: `${pct * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {excluded.length > 0 && (
        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <div className="font-medium">Excluding rules applied</div>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {excluded.map((e) => (
                <span
                  key={e}
                  className="rounded-md bg-white px-1.5 py-0.5 font-mono text-[11px] text-amber-700"
                >
                  {e}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function barColor(pct: number, earned: number): string {
  if (earned === 0) return "bg-slate-200";
  if (pct >= 0.75) return "bg-emerald-500";
  if (pct >= 0.4) return "bg-indigo-500";
  return "bg-amber-500";
}
